import {
  Box, Button, Flex, HStack, Input, InputGroup, InputLeftElement,
  Table, Thead, Tbody, Tr, Th, Td,
  Text, Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { Search } from "lucide-react";
import { useLeadersAdmin } from "./useLeadersAdmin";
import { ErrorState } from "../../../components/ErrorState";
import { ListPagination } from "../../rh/onboarding/components/ListPagination";
import { OnflyLoading } from "../../../tools/OnflyLoading";

export function LeadersAdminPage() {
  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const subtleColor = useColorModeValue("#84858c", "#718096");
  const thColor     = useColorModeValue("#84858c", "#718096");
  const cardBg      = useColorModeValue("white", "#0f2040");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const theadBg     = useColorModeValue("rgba(0,158,251,0.04)", "rgba(0,158,251,0.06)");
  const rowHoverBg  = useColorModeValue("rgba(0,158,251,0.025)", "rgba(0,158,251,0.04)");
  const inputBg     = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");

  const {
    leaders, loading, error, syncing, search, setSearch,
    page, totalPages, setPage, handleSync, refreshList,
  } = useLeadersAdmin();

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="flex-start" mb={6} gap={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight={700} color={titleColor} mb={1}>
            Gestores
          </Text>
          <Text fontSize="sm" color={descColor}>
            Gestores sincronizados do Sankhya. Use o botão para atualizar a lista.
          </Text>
        </Box>
        <Button
          leftIcon={<RepeatIcon />}
          bg="#009efb"
          color="white"
          _hover={{ bg: "#007bd4" }}
          borderRadius="50px"
          size="sm"
          flexShrink={0}
          isLoading={syncing}
          loadingText="Sincronizando..."
          onClick={handleSync}
        >
          Sincronizar
        </Button>
      </Flex>

      <HStack spacing={3} mb={6} maxW="480px">
        <InputGroup flex={1}>
          <InputLeftElement pointerEvents="none">
            <Icon as={Search} boxSize={4} color={subtleColor} />
          </InputLeftElement>
          <Input
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            bg={inputBg}
            borderColor={inputBorder}
            borderRadius="8px"
            fontSize="sm"
            _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
            _placeholder={{ color: subtleColor }}
          />
        </InputGroup>
      </HStack>

      {loading ? (
        <Flex justify="center" align="center" py={16}>
          <OnflyLoading containerSize={80} />
        </Flex>
      ) : error ? (
        <ErrorState onRetry={refreshList} />
      ) : leaders.length === 0 ? (
        <Text color={subtleColor} fontSize="sm">
          {search ? "Nenhum gestor encontrado para a busca." : "Nenhum gestor cadastrado. Clique em Sincronizar para importar do Sankhya."}
        </Text>
      ) : (
        <Box
          bg={cardBg}
          borderRadius="12px"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="0px 4px 8px 0px rgba(14, 61, 93, 0.08)"
          overflow="hidden"
        >
          <Table variant="simple">
            <Thead bg={theadBg}>
              <Tr>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Nome</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>E-mail</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>CPF</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3} isNumeric>Liderados</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3} isNumeric>Cód. Func.</Th>
              </Tr>
            </Thead>
            <Tbody>
              {leaders.map((leader) => (
                <Tr key={leader.id} _hover={{ bg: rowHoverBg }} transition="background 0.12s">
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontWeight={600} fontSize="sm" color={titleColor}>
                      {leader.nome_lider || "—"}
                    </Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontSize="sm" color={descColor}>{leader.email_lider || "—"}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontSize="sm" color={descColor}>{leader.cpf_lider || "—"}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5} isNumeric>
                    <Text fontSize="sm" color={descColor}>{leader.qtd_liderados ?? "—"}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5} isNumeric>
                    <Text fontSize="sm" color={subtleColor}>{leader.codfunc_lider ?? "—"}</Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </Box>
  );
}
